import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Plus, FileText, TrendingUp, BookOpen, CheckCircle2, Send, Clock,
  Loader2, ArrowRight, Trash2, Building2, Inbox, Sparkles,
} from "lucide-react";
import api, { apiError } from "@/lib/api";
import { money, statusMeta } from "@/lib/format";
import { ReferralBanner } from "@/components/ReferralBanner";
import { PriceAutomaticallyButton } from "@/components/PriceAutomaticallyButton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

function StatCard({ icon: Icon, label, value, tint, delay }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay }}
      className="bg-card border border-border rounded-xl p-5 shadow-sm"
    >
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center mb-3 ${tint}`}>
        <Icon className="w-5 h-5" />
      </div>
      <div className="text-2xl font-bold font-display text-slate-900 dark:text-slate-100">{value}</div>
      <div className="text-xs uppercase tracking-wider text-muted-foreground mt-1">{label}</div>
    </motion.div>
  );
}

export default function Dashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [rfqs, setRfqs] = useState(null);
  const [stats, setStats] = useState(null);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: "", buyer_name: "", buyer_address: "", notes: "" });
  const [delId, setDelId] = useState(null);
  const [invites, setInvites] = useState([]);
  const [busyInvite, setBusyInvite] = useState("");
  const dashCreated = useRef({});

  const cur = user?.currency || "$";
  const catEmpty = stats?.catalogue_count === 0;

  const load = () => {
    api.get("/rfqs").then((r) => setRfqs(r.data)).catch(() => setRfqs([]));
    api.get("/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/vendor/opportunities")
      .then((r) => setInvites((r.data.opportunities || []).filter((o) => o.my_status === "invited")))
      .catch(() => setInvites([]));
  };
  useEffect(load, []);

  const priceInvite = async (o) => {
    setBusyInvite(o.id);
    try {
      const { data } = await api.post(`/vendor/opportunities/${o.id}/start`);
      if (data.ai_skipped) {
        toast.warning(`Items loaded without AI pricing — your ${data.plan_name} plan's ${data.quota} automatic quotes are used up this month`);
      } else if (!data.reused) {
        toast.success(`Priced ${data.matched} of ${data.total} lines from your catalogue`);
      }
      navigate(`/rfq/${data.rfq_id}`);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not start the quotation");
    } finally {
      setBusyInvite("");
    }
  };

  const createRfq = async () => {
    if (!form.name.trim()) return toast.error("Quote name is required");
    setSaving(true);
    try {
      const { data } = await api.post("/rfqs", form);
      toast.success("Quote created");
      setOpen(false);
      setForm({ name: "", buyer_name: "", buyer_address: "", notes: "" });
      navigate(`/rfq/${data.id}`);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/rfqs/${delId}`);
      toast.success("RFQ deleted");
      setDelId(null);
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  return (
    <div>
      <ReferralBanner />
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
        <div>
          <p className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Dashboard</p>
          <h1 className="font-display text-3xl font-extrabold text-slate-900 dark:text-slate-100">
            Active RFQs
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Manage all your quotation requests in one place.
          </p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-rfq-button" className="h-11 gap-2 font-semibold active:scale-[0.98] transition-transform">
              <Plus className="w-4 h-4" /> New Quote
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-card">
            <DialogHeader>
              <DialogTitle className="font-display">Create new quote</DialogTitle>
              <DialogDescription>Start a new quotation request for a contractor.</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div>
                <Label>Quote / Project Name *</Label>
                <Input
                  data-testid="rfq-name-input"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="e.g. Office Tower - Electrical Package"
                  className="mt-1.5"
                />
              </div>
              <div>
                <Label>Contractor Name</Label>
                <Input
                  data-testid="rfq-buyer-input"
                  value={form.buyer_name}
                  onChange={(e) => setForm({ ...form, buyer_name: e.target.value })}
                  placeholder="e.g. Skyline Developers LLC"
                  className="mt-1.5"
                />
              </div>
              <div>
                <Label>Contractor Address</Label>
                <Textarea
                  value={form.buyer_address}
                  onChange={(e) => setForm({ ...form, buyer_address: e.target.value })}
                  placeholder="Contractor address"
                  className="mt-1.5"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button data-testid="create-rfq-submit" onClick={createRfq} disabled={saving}>
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create Quote"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {invites.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          data-testid="new-rfq-invites"
          className="mb-8 rounded-xl border border-brand/40 bg-brand-soft/50 overflow-hidden"
        >
          <div className="flex items-center justify-between gap-3 px-5 py-3 border-b border-brand/25">
            <div className="flex items-center gap-2 text-sm font-bold text-brand">
              <Inbox className="w-4 h-4" />
              {invites.length} new RFQ{invites.length === 1 ? "" : "s"} from contractors
            </div>
            <button data-testid="view-all-invites" onClick={() => navigate("/tracker")} className="text-xs font-bold text-brand hover:underline">
              View in RFQ Tracker
            </button>
          </div>
          <div className="divide-y divide-brand/15">
            {invites.slice(0, 3).map((o, i) => (
              <div key={o.id} data-testid={`dashboard-invite-${i}`} className="flex flex-col sm:flex-row sm:items-center gap-3 px-5 py-3.5">
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-slate-900 dark:text-slate-100 truncate">{o.title}</div>
                  <div className="text-xs text-muted-foreground mt-0.5">
                    {[o.buyer_company, o.rfq_ref, `${o.item_count} items`, o.due_date && `due ${o.due_date}`].filter(Boolean).join(" · ")}
                  </div>
                </div>
                {o.item_count > 0 ? (
                  <div className="flex items-center gap-2 shrink-0">
                    <PriceAutomaticallyButton
                      testid={`dashboard-price-invite-${i}`}
                      className="h-10 gap-2 font-semibold"
                      align="end"
                      catEmpty={catEmpty}
                      getRfqId={async () => {
                        const { data } = await api.post(`/vendor/opportunities/${o.id}/start`, null, { params: { mode: "manual" } });
                        dashCreated.current[o.id] = data.rfq_id;
                        return data.rfq_id;
                      }}
                      onCatalogue={async () => { await priceInvite(o); }}
                      onPriced={() => { const rid = dashCreated.current[o.id]; if (rid) navigate(`/rfq/${rid}`); }}
                    />
                  </div>
                ) : (
                  <Button variant="outline" onClick={() => navigate("/tracker")} className="h-10 shrink-0">View files</Button>
                )}
              </div>
            ))}
          </div>
        </motion.div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard icon={FileText} label="Active RFQs" value={stats?.rfq_count ?? "—"} tint="bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300" delay={0.05} />
        <StatCard icon={TrendingUp} label="Total Value" value={stats ? money(stats.total_value, cur) : "—"} tint="bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-300" delay={0.1} />
        <StatCard icon={CheckCircle2} label="Accepted" value={stats?.by_status?.accepted ?? "—"} tint="bg-violet-100 text-violet-700 dark:bg-violet-900/50 dark:text-violet-300" delay={0.15} />
        <StatCard icon={BookOpen} label="Catalogue Items" value={stats?.catalogue_count ?? "—"} tint="bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-300" delay={0.2} />
      </div>

      {rfqs === null ? (
        <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
      ) : rfqs.length === 0 ? (
        <div className="text-center py-20 border-2 border-dashed border-border rounded-xl">
          <Building2 className="w-12 h-12 mx-auto text-muted-foreground/40" />
          <p className="mt-4 font-semibold text-slate-900 dark:text-slate-100">No quotes yet</p>
          <p className="text-sm text-muted-foreground mt-1">Create your first quote to start pricing.</p>
          <Button className="mt-5 gap-2" onClick={() => setOpen(true)}>
            <Plus className="w-4 h-4" /> New Quote
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="rfq-grid">
          {rfqs.map((r, i) => {
            const sm = statusMeta[r.status] || statusMeta.draft;
            return (
              <motion.div
                key={r.id}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: Math.min(i * 0.04, 0.3) }}
                data-testid={`rfq-card-${r.id}`}
                onClick={() => navigate(`/rfq/${r.id}`)}
                className="group bg-card border border-border rounded-xl p-5 shadow-sm hover:shadow-md hover:border-primary/40 transition-all cursor-pointer flex flex-col"
              >
                <div className="flex items-start justify-between gap-3 mb-3">
                  <span className={`px-2.5 py-1 rounded-md text-xs font-semibold ${sm.cls}`}>{sm.label}</span>
                  <span className="font-mono text-xs text-muted-foreground">{r.quote_ref}</span>
                </div>
                <h3 className="font-display font-bold text-base text-slate-900 dark:text-slate-100 leading-snug line-clamp-2">
                  {r.name}
                </h3>
                <p className="text-sm text-muted-foreground mt-1 truncate">{r.buyer_name || "No buyer set"}</p>

                <div className="flex items-center gap-4 mt-4 pt-4 border-t border-border">
                  <div>
                    <div className="text-lg font-bold font-mono text-slate-900 dark:text-slate-100">
                      {money(r.total, r.currency)}
                    </div>
                    <div className="text-[11px] text-muted-foreground uppercase tracking-wider">
                      {r.item_count} items
                    </div>
                  </div>
                  <div className="ml-auto flex items-center gap-1">
                    <button
                      data-testid={`delete-rfq-${r.id}`}
                      onClick={(e) => { e.stopPropagation(); setDelId(r.id); }}
                      className="w-8 h-8 rounded-lg flex items-center justify-center text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center text-primary group-hover:translate-x-0.5 transition-transform">
                      <ArrowRight className="w-4 h-4" />
                    </div>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      )}

      <AlertDialog open={!!delId} onOpenChange={(o) => !o && setDelId(null)}>
        <AlertDialogContent className="bg-card">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this RFQ?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove the RFQ and all its line items. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-rfq" onClick={doDelete} className="bg-destructive hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
